import { z } from 'zod'

import {
  accept,
  isPlayersTurn,
  nextPlayerId,
  reject,
  type ActionContext,
  type GameEngine,
  type PlaySummary,
  type TurnState,
} from '../engine.js'
import type { Rng } from '../rng.js'
import { TEDDY_KEYS, type TeddyKey } from '../teddies.js'

/**
 * Sky's Memory Match.
 *
 * The cards are photographs of Hazel's own teddies, which is where the personal
 * touch actually lands: the whole collection is in the game even though only
 * Sky's name is on the door.
 */

/**
 * How long a mismatched pair stays face up.
 *
 * The whole game is remembering what you saw, so this has to be long enough to
 * actually look at two cards and place them. Enforced here rather than in the
 * browser: a client that flipped again immediately would rob everybody else in
 * the room of the one moment the game is about.
 */
export const REVEAL_MS = 2_500

/** Board sizes, so a younger cousin can play the same game. */
/**
 * How many cards a board can have.
 *
 * Every one divides by four, because the phone draws four columns and a
 * part-filled row is awful to arrow through — Down from the last full row
 * lands on nothing.
 *
 * Forty is the ceiling, and not because of the screen. Twenty-one teddies
 * would allow twenty-one pairs, but forty-two does not divide by four; and it
 * only fits four rows if drawn ten-and-a-half columns wide. Drawing it
 * fourteen wide for the television while the cursor stepped by seven is the
 * diagonal this game already had once. So forty it is — twenty pairs of
 * twenty-one, with one teddy sitting out each game, which keeps even the top
 * board different every time.
 */
export const MEMORY_SIZES = [8, 12, 16, 20, 24, 32, 40] as const

export type MemorySize = (typeof MEMORY_SIZES)[number]

export const DEFAULT_MEMORY_SIZE: MemorySize = 20

export function isMemorySize(value: unknown): value is MemorySize {
  return typeof value === 'number' && (MEMORY_SIZES as readonly number[]).includes(value)
}

export interface MemoryConfig {
  readonly size?: MemorySize
  readonly startedAt: number
}

export interface MemoryState {
  /**
   * Every card, in board order, with the teddy on its face.
   *
   * The secret. This never leaves the server in a room — toMemoryView is the
   * only way out, and it shows a face only once somebody has turned it over.
   */
  readonly cards: readonly TeddyKey[]
  /** Card positions found and left face up for good. */
  readonly matched: readonly number[]
  /** Positions turned over right now and not yet matched. At most two. */
  readonly faceUp: readonly number[]
  readonly status: 'playing' | 'finished'
  readonly turn: TurnState
  /** Every card turned over, which is what the score is really about. */
  readonly flips: number
  readonly playerPairs: Readonly<Record<string, number>>
  readonly startedAt: number
  /**
   * When a mismatched pair may be turned back over.
   *
   * Null when nothing is waiting. Until it passes, flips are refused — which is
   * what makes the pause part of the game rather than a suggestion.
   */
  readonly revealUntil: number | null
}

/** The largest board there is, so the schema cannot drift from the sizes. */
/**
 * The most cards a board can hold, and so twice the most teddies it can want.
 *
 * Exported because two tests worked it out for themselves rather than asking —
 * one with `Math.max`, the other by taking the last entry, which quietly
 * assumes the sizes are written in order. Three answers to one question is how
 * they come to disagree.
 */
// Reduced rather than `Math.max`, which widens to `number` and would make
// this unusable anywhere a board size is asked for.
export const LARGEST_BOARD = MEMORY_SIZES.reduce((most, size) => (size > most ? size : most))

export const memoryAction = z.object({
  type: z.literal('flip'),
  card: z
    .number()
    .int()
    .min(0)
    .max(LARGEST_BOARD - 1),
})

export type MemoryAction = z.infer<typeof memoryAction>

/** One card, as a client is allowed to see it. */
export interface MemoryCardView {
  readonly card: number
  /** Null while it is face down, which is the entire game. */
  readonly teddy: TeddyKey | null
  readonly matched: boolean
}

export interface MemoryView {
  readonly cards: readonly MemoryCardView[]
  readonly status: MemoryState['status']
  readonly currentPlayerId: string | null
  readonly flips: number
  readonly pairsFound: number
  readonly pairsTotal: number
  readonly playerPairs: Readonly<Record<string, number>>
  /** When the pair showing now turns back over, so a client can count down. */
  readonly revealUntil: number | null
  readonly roundPlayerIds: readonly string[]
}

/**
 * Deals the board.
 *
 * Each teddy appears exactly twice, and the shuffle is seeded, so a room can be
 * replayed and a test can know what it dealt.
 */
function deal(size: MemorySize, rng: Rng): readonly TeddyKey[] {
  const pairs = size / 2
  // Which teddies are in play is shuffled too, so a small board is not always
  // the same four teddies.
  const chosen = rng.shuffle(TEDDY_KEYS).slice(0, pairs)

  return rng.shuffle([...chosen, ...chosen])
}

/**
 * Turns a mismatched pair back over, once its moment has passed.
 *
 * Pulled out because two things need it: the next flip, which must not act on a
 * board still showing the last one, and the room's sweep, so a board nobody is
 * touching does not sit with two cards showing indefinitely.
 */
export function settleReveal(state: MemoryState, now: number): MemoryState {
  if (state.faceUp.length < 2) return state
  if (state.revealUntil !== null && now < state.revealUntil) return state

  return { ...state, faceUp: [], revealUntil: null }
}

function settle(state: MemoryState): MemoryState {
  if (state.matched.length < state.cards.length) return state
  return { ...state, status: 'finished', turn: { ...state.turn, currentPlayerId: null } }
}

export const memoryEngine: GameEngine<MemoryConfig, MemoryState, MemoryAction> = {
  key: 'memory',
  actionSchema: memoryAction,

  createInitialState(config, players, rng): MemoryState {
    const size = config.size ?? DEFAULT_MEMORY_SIZE

    return {
      cards: deal(size, rng),
      matched: [],
      faceUp: [],
      status: 'playing',
      turn: { players, currentPlayerId: players[0]?.id ?? null },
      flips: 0,
      playerPairs: Object.fromEntries(players.map((player) => [player.id, 0])),
      startedAt: config.startedAt,
      revealUntil: null,
    }
  },

  reduce(state, action, context: ActionContext) {
    if (state.status !== 'playing') return reject(state, 'game-over')
    if (!isPlayersTurn(state.turn, context.playerId)) return reject(state, 'not-your-turn')

    // A pair is still showing. Turning another card now would take away the one
    // moment this game is about, so it waits — for everybody, not just for
    // whoever was quickest to tap.
    if (
      state.faceUp.length === 2 &&
      state.revealUntil !== null &&
      context.now < state.revealUntil
    ) {
      return reject(state, 'too-soon')
    }

    const board = settleReveal(state, context.now)

    if (action.card >= board.cards.length) return reject(state, 'invalid-action')
    // Already face up, or already found. A misclick on a phone, not a mistake.
    if (board.matched.includes(action.card) || board.faceUp.includes(action.card)) {
      return reject(state, 'already-played')
    }

    const teddy = board.cards[action.card]
    if (teddy === undefined) return reject(state, 'invalid-action')

    const flips = board.flips + 1

    // The first of a pair. Nothing to compare it against yet.
    if (board.faceUp.length === 0) {
      return accept({ ...board, faceUp: [action.card], flips })
    }

    const first = board.faceUp[0] as number

    if (board.cards[first] === teddy) {
      // Found. Another go, as in every version of this game anybody has played
      // — which is also what makes remembering worth doing.
      return accept(
        settle({
          ...board,
          matched: [...board.matched, first, action.card],
          faceUp: [],
          flips,
          playerPairs: {
            ...board.playerPairs,
            [context.playerId]: (board.playerPairs[context.playerId] ?? 0) + 1,
          },
          revealUntil: null,
        }),
      )
    }

    // A miss. Both stay up for a moment, then the go passes.
    return accept({
      ...board,
      faceUp: [first, action.card],
      flips,
      revealUntil: context.now + REVEAL_MS,
      turn: { ...board.turn, currentPlayerId: nextPlayerId(board.turn) },
    })
  },

  isTerminal: (state) => state.status !== 'playing',

  summarise(state, endedAt): PlaySummary {
    return {
      gameKey: 'memory',
      result: state.status === 'finished' ? 'won' : 'abandoned',
      durationMs: Math.max(0, endedAt - state.startedAt),
      difficulty: `${state.cards.length} cards`,
    }
  },
}

/**
 * Strips the board before it leaves the server.
 *
 * Sending the whole state to a browser would put every card's face in devtools,
 * and the entire game is not knowing what the other player does not. A face
 * appears here only once somebody has turned that card over.
 */
export function toMemoryView(state: MemoryState): MemoryView {
  return {
    cards: state.cards.map((teddy, card) => {
      const matched = state.matched.includes(card)
      const showing = matched || state.faceUp.includes(card)

      return { card, teddy: showing ? teddy : null, matched }
    }),
    status: state.status,
    currentPlayerId: state.turn.currentPlayerId,
    flips: state.flips,
    pairsFound: state.matched.length / 2,
    pairsTotal: state.cards.length / 2,
    playerPairs: state.playerPairs,
    revealUntil: state.revealUntil,
    roundPlayerIds: state.turn.players.map((player) => player.id),
  }
}

/**
 * Passes the go without anybody having played.
 *
 * Used when a turn timer runs out. Deliberately not an action, for the same
 * reason as in Hangman: a client that could send this could skip other
 * people's turns.
 */
export function skipMemoryTurn(state: MemoryState, now: number): MemoryState {
  if (state.status !== 'playing') return state

  const passed = { ...state, turn: { ...state.turn, currentPlayerId: nextPlayerId(state.turn) } }

  // A missed pair that is still inside its moment stays up.
  //
  // A miss hands the go straight to the next player, so if *their* phone locks
  // a second later the room skips past them — and clearing the pair here would
  // take the look away from everybody else, who are sitting watching the
  // television doing exactly what the game asked of them. The pair goes back
  // down when its time is up, which is what settleReveal is for.
  if (state.revealUntil !== null && now < state.revealUntil) return passed

  return {
    ...passed,
    // Otherwise whatever was showing goes back down with the go. Leaving a card
    // up would hand the next player a free look they did not earn.
    faceUp: [],
    revealUntil: null,
  }
}
